import {Component, EventEmitter, inject, Input, OnChanges, Output, SimpleChanges, ChangeDetectionStrategy} from '@angular/core';
import {BicBadgeComponent, BicButtonComponent, BicPopupComponent} from "@buildinginformationcloud/bic-ui-components";
import {
  CheckResultControllerService,
  Contingent,
  Price,
  Product,
  ProductDto,
  SimpleCheck
} from "../../../api";
import {DatePipe} from "@angular/common";
import {StateConfigService} from "../../../services/state/state-config.service";
import {CheckTypePipe} from "../../../pipes/check-type.pipe";
import {
  ContingentUsage,
  ContingentUsageService,
  LimitUnit,
  TenantLimit
} from "../../../services/contingent-usage.service";

/**
 * Eine Grenze, fertig fuer die Anzeige.
 *
 * <p>Die Umrechnung passiert hier und nicht im Template: das Template kennt nur noch Zeilen und
 * muss nicht wissen, ob hinter einer Grenze Bytes oder Stueckzahlen stecken. Genau dafuer liefert
 * der Endpunkt seit dem Umbau eine Liste - eine neue Grenze soll ohne Template-Aenderung
 * erscheinen.</p>
 */
export interface LimitRow {
  key: string;
  label: string;
  /** Rechte Seite der Kopfzeile: "3,4 GB / 200 GB" bzw. "2 / unbegrenzt". */
  valueLabel: string;
  hasLimit: boolean;
  percent: number;
  /** Ab 90 % - dann faerbt sich der Balken, statt still vollzulaufen. */
  tight: boolean;
  /** Restzeile; {@code null}, wenn es keine Grenze gibt und damit nichts zu melden ist. */
  remainingLabel: string | null;
  /** Dezenter Zusatz zur Herkunft; {@code null} im Normalfall. */
  sourceLabel: string | null;
}

@Component({
  selector: 'app-shop-info',
  imports: [
    BicButtonComponent,
    BicPopupComponent,
    BicBadgeComponent,
    DatePipe,
    CheckTypePipe
  ],
  templateUrl: './checkout-info.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './checkout-info.component.scss'
})
export class CheckoutInfoComponent implements OnChanges {

  stateConfigService = inject(StateConfigService);
  checkResultControllerService = inject(CheckResultControllerService);
  contingentUsageService = inject(ContingentUsageService);

  @Input() isOpen: boolean = false;
  @Output() isOpenChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  @Input() contingent!: Contingent
  @Input() activePrice!: Price
  @Input() activeProductSettings!: ProductDto
  @Input() activeProduct!: Product
  @Input() name: string = ''
  @Input() isYearly: boolean = false

  checks: SimpleCheck[] = []

  /**
   * Verbrauch gegen die mengenmaessigen Grenzen - erst nach dem Laden gesetzt.
   *
   * <p>Bewusst NICHT aus `contingent` gerechnet: dort stehen nur die Grenzen, und die verbrauchte
   * Seite kennt das Frontend gar nicht. Der Speicherstand im `StateConfigService` waere zudem der
   * des AUSGEWAEHLTEN PROJEKTS - das Limit gilt aber fuer den ganzen Mandanten. Wer drei Projekte
   * hat, saehe dort ein Drittel der Wahrheit.</p>
   */
  usage: ContingentUsage | null = null;

  get organisationName(): string {
    return this.stateConfigService.isPersonal ? 'Persönlich' : this.stateConfigService.getOrganisation()?.name || '';
  }

  /**
   * Geladen wird beim OEFFNEN, nicht beim Erzeugen der Komponente.
   *
   * <p>Der Unterschied ist nicht theoretisch: {@code <app-shop-info>} haengt im Shop-Template
   * dauerhaft (nur der Dialog darin ist an {@code isOpen} gebunden). In {@code ngOnInit} lief der
   * Abruf also EINMAL beim Laden der Shop-Seite - wer danach etwas am Kontingent aenderte und den
   * Dialog erneut oeffnete, sah weiter die Zahlen von vorhin und musste raten, ob seine Aenderung
   * angekommen ist. Genau darueber ist am 2026-09-17 jemand gestolpert.</p>
   *
   * <p>Nebeneffekt, der auch ohne den Fehler zaehlt: die vier Abrufe laufen nur noch, wenn der
   * Dialog wirklich aufgeht, statt bei jedem Besuch der Shop-Seite.</p>
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen']?.currentValue === true) {
      this.load();
    }
  }

  private load(): void {

    // Der Shop ist bewusst org- bzw. nutzerweit: die Kontingente haengen an der Organisation
    // (StateConfigService.contingent), nicht am Projekt. Deshalb hier fest `undefined` statt
    // getProjectIdOrUndefined() -- vorher erzwang der CheckoutComponent denselben Scope, indem er
    // die Projektauswahl global geleert hat.
    // API-Typ fordert `number`, org-/persoenlich-weite Historie laeuft hier bewusst ohne Projekt.
    const projectId = undefined as unknown as number;
    // Bei jedem Oeffnen frisch: sonst bleibt die Historie beim Wiederoeffnen veraltet.
    this.checks = [];
    this.checkResultControllerService.getCheckResultsAsSimpleChecks(projectId, 'ALL').subscribe({
      next: checks => {
        this.checks = [...checks].sort((a, b) => {
          const dateA = a.checkDate ? new Date(a.checkDate).getTime() : 0;
          const dateB = b.checkDate ? new Date(b.checkDate).getTime() : 0;
          return dateB - dateA;
        });
      },
      error: () => {
        this.checks = [];
      }
    });
    const organisationId = this.stateConfigService.getOrganisationIdOrUndefined();

    // Derselbe Mandantenbezug wie oben: die Organisation, deren Abo dieser Dialog zeigt - ohne sie
    // das persoenliche Konto. Scheitert der Aufruf, bleibt der Block einfach aus; er ist eine
    // Zusatzauskunft und darf den Rest des Dialogs nicht mitnehmen.
    this.contingentUsageService.getUsage(organisationId).subscribe({
      next: usage => this.usage = usage,
      error: () => this.usage = null,
    });
  }

  // ------------------------------------------------------------------ Grenzen

  /**
   * Die Grenzen, wie sie angezeigt werden - eine Zeile je gelieferter Grenze.
   *
   * <p>Es gibt hier bewusst keine feste Reihenfolge und keine Auswahl bekannter Schluessel: was
   * der Server schickt, wird gezeigt, und zwar in seiner Reihenfolge. Sonst waere der Umbau des
   * Endpunkts auf eine Liste umsonst gewesen - eine neue Grenze muesste doch wieder hier
   * eingetragen werden.</p>
   *
   * <p>Ist die Liste leer (kein Zugriff, kein Kontingent), blendet das Template den ganzen Block
   * samt Ueberschrift aus.</p>
   */
  get limitRows(): LimitRow[] {
    return (this.usage?.limits ?? []).map(limit => this.toRow(limit));
  }

  private toRow(limit: TenantLimit): LimitRow {
    const hasLimit = limit.limit !== null;
    const percent = this.percentOf(limit);
    const used = this.formatValue(limit.used, limit.unit);

    return {
      key: limit.key,
      label: limit.label,
      valueLabel: hasLimit
        ? `${used} / ${this.formatValue(limit.limit!, limit.unit)}`
        : `${used} / unbegrenzt`,
      hasLimit,
      percent,
      tight: hasLimit && this.isTight(percent),
      remainingLabel: hasLimit ? this.remainingLabel(limit) : null,
      sourceLabel: this.sourceLabel(limit),
    };
  }

  /** Ab hier wird es eng - dann faerbt sich der Balken, statt still vollzulaufen. */
  isTight(percent: number): boolean {
    return percent >= 90;
  }

  private percentOf(limit: TenantLimit): number {
    if (limit.limit === null) {
      return 0;
    }
    // Eine Grenze von 0 laesst sich nicht anteilig darstellen. Sie voll und rot zu zeigen ist die
    // ehrlichere Antwort als ein leerer Balken: es ist nichts frei und wird auch nichts frei.
    if (limit.limit <= 0) {
      return 100;
    }
    return Math.min(100, Math.round((limit.used / limit.limit) * 100));
  }

  /**
   * Zahl mit Einheit, lesbar.
   *
   * <p>Bei Bytes richtet sich die Einheit nach der Groesse: "0,2 GB" bei einem angefangenen
   * Projekt sagt nichts, "312 MB" schon. Umgerechnet wird in 1024er-Schritten, also mit derselben
   * Basis, mit der der Server das Limit prueft.</p>
   */
  private formatValue(value: number, unit: LimitUnit): string {
    if (unit !== 'BYTES') {
      return String(value);
    }
    const mb = value / (1024 * 1024);
    if (mb < 1024) {
      return `${mb.toLocaleString('de-DE', {maximumFractionDigits: 1})} MB`;
    }
    return `${(mb / 1024).toLocaleString('de-DE', {maximumFractionDigits: 1})} GB`;
  }

  /**
   * Die Zahl, nach der hier wirklich gefragt wird: wie viel noch geht.
   *
   * <p>Bei 0 reicht die Zahl nicht - dann muss dastehen, was zu tun ist. Wer im Shop liest
   * "Noch 0 Vorlagen moeglich", sucht als naechstes den Knopf, den es nicht gibt.</p>
   */
  private remainingLabel(limit: TenantLimit): string {
    const remaining = limit.remaining ?? 0;

    if (remaining <= 0) {
      switch (limit.key) {
        case 'myCheckTemplates':
          return 'Keine weitere Vorlage möglich – bitte eine bestehende löschen oder das Abo erweitern.';
        case 'storage':
          return 'Kein Speicher mehr frei – bitte Dateien löschen oder das Abo erweitern.';
        default:
          // Unbekannter Schluessel: die Grenze kennt das Frontend nicht, das Aufraeumen also auch
          // nicht. Dann lieber das Label des Servers zitieren als eine falsche Anweisung geben.
          return `Grenze erreicht – „${limit.label}" lässt sich erst nach dem Aufräumen oder einer Abo-Erweiterung weiter nutzen.`;
      }
    }

    if (limit.key === 'myCheckTemplates') {
      return `Noch ${remaining} ${remaining === 1 ? 'Vorlage' : 'Vorlagen'} möglich`;
    }
    return `Noch ${this.formatValue(remaining, limit.unit)} frei`;
  }

  /**
   * Woher die Grenze kommt - aber nur, wenn es nicht der Normalfall ist.
   *
   * <p>Am 2026-09-17 stand jemand ratlos vor diesem Dialog: oben stand sein Plan, die Grenze
   * darunter passte nicht dazu, weil sie einzeln fuer ihn gesetzt worden war. Ohne diesen Zusatz
   * sieht die Anzeige schlicht falsch aus. {@code PLAN} bleibt deshalb stumm - dass eine Grenze
   * aus dem gebuchten Plan stammt, ist die Erwartung und keine Meldung wert; {@code UNLIMITED}
   * ebenso, denn das steht bereits als "unbegrenzt" in der Zeile.</p>
   */
  private sourceLabel(limit: TenantLimit): string | null {
    switch (limit.source) {
      case 'OVERRIDE':
        return 'individuell festgelegt';
      case 'DEFAULT':
        return 'Standardwert';
      default:
        return null;
    }
  }

  /** Abo-Credits plus die nicht verfallenden Add-on-Credits. */
  get totalCredits(): number {
    return (this.contingent?.credits ?? 0) + (this.addOnCredits);
  }

  /**
   * Der nicht verfallende Anteil des Guthabens.
   *
   * <p>Wird nur angezeigt, wenn er vorhanden ist: eine Zeile „0 Credits, die nicht verfallen"
   * erklärt niemandem etwas.
   */
  get addOnCredits(): number {
    return this.contingent?.addOnCredits ?? 0;
  }

  /**
   * Wie viele Nutzungen eines Moduls das Guthaben noch hergibt.
   *
   * <p>Über das Gesamtguthaben (Abo-Credits **plus** Add-on-Credits) - so bucht der Server ab
   * (`Contingent.spendCredits`). Vorher stand hier nur `credits`, wodurch bezahltes
   * Add-on-Guthaben in dieser Übersicht schlicht nicht auftauchte.
   */
  numberOfChecks(costsPerCheck: number | undefined): number {
    if (!costsPerCheck) {
      return 0;
    }
    return Math.floor(this.totalCredits / costsPerCheck);
  }

  /**
   * Rechnet einen Credit-Betrag in Prüfungen um.
   *
   * <p>Der Faktor ist der myCheck-Pauschalpreis aus dem Kontingent (`module_credit_cost.MY_CHECK`),
   * bewusst nicht hartcodiert: er ist seit V26 datengesteuert. Ist er nicht lesbar oder 0, bleibt
   * der Betrag stehen, statt eine Division durch 0 anzuzeigen.
   */
  asChecks(credits: number | undefined): number {
    const costPerCheck = this.contingent?.myCheckCosts ?? 0;
    if (!credits) return 0;
    return costPerCheck > 0 ? Math.round(credits / costPerCheck) : credits;
  }

  /** Prüfungen, die der aktive Plan pro Zyklus enthält – die Bezugsgröße der "x / y"-Anzeige. */
  get totalChecks(): number {
    return this.asChecks(this.activeProductSettings?.credits);
  }

  closePopup() {
    this.isOpenChange.emit(false);
  }
}
